import type { OrderLineItemSegment } from "./order-line-items";

export const RECEIVE_PDF_WIDTH_MM = 182;
export const RECEIVE_PDF_HEIGHT_MM = 257;
export const RECEIVE_PDF_RENDER_WIDTH_PX = 728;
export const RECEIVE_PDF_RENDER_HEIGHT_PX = 1028;
export const DAILY_ORDER_PRINT_PDF_WIDTH_MM = 297;
export const DAILY_ORDER_PRINT_PDF_HEIGHT_MM = 210;
export const DAILY_ORDER_PRINT_RENDER_WIDTH_PX = 1120;
export const DAILY_ORDER_PRINT_RENDER_HEIGHT_PX = 792;
export const DAILY_ORDER_PRINT_ROWS_PER_PAGE = 10;
export const RECEIVE_PDF_PROPERTY_VALUE_CHARS = 220;
export const RECEIVE_PDF_PROPERTY_GROUP_CHARS = 360;
export const RECEIVE_PDF_NOTE_CHARS = 700;
export const RECEIVE_PDF_ATELIER_TASKS = [
  "袖付け",
  "装飾追加",
  "肩詰め",
  "オーバースカート",
  "ボレロ",
  "丈直し",
] as const;
export const RECEIVE_PDF_ADDITIONAL_OPTIONS = [
  "内側ゴムベルト",
  "取り外しゴムベルト",
  "バストライン変更",
  "カラー変更",
] as const;

export function allowsDetachedSizeOrderPdfInspectionFooter(
  salesSource: string,
  targetSkuCount: number,
): boolean {
  return salesSource.trim() === "ネット" && targetSkuCount >= 2;
}

export function hasReceivePdfNoteContent(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/** Split only the current order/unit's unshipped items; never copy siblings. */
export function partitionReceivePdfItemSegments<T extends { id: string }>(
  unshipped: Array<OrderLineItemSegment<T>>,
  targetItemIds: ReadonlySet<string>,
): { itemSegments: Array<OrderLineItemSegment<T>>; bundledSegments: Array<OrderLineItemSegment<T>> } {
  const targets = unshipped.filter(({ item }) => targetItemIds.has(item.id));
  // Preserve the existing fallback for orders without a printable target SKU.
  if (targets.length === 0) return { itemSegments: unshipped, bundledSegments: [] };
  return {
    itemSegments: targets,
    bundledSegments: unshipped.filter(({ item }) => !targetItemIds.has(item.id)),
  };
}

export type ReceivePdfLayout = "size-order" | "repair";

export interface ReceivePdfLayoutOptions {
  instructionTitle: string;
  showInspectionFooter: boolean;
}

export interface ReceivePdfPageGroupPlan {
  pageCount: number;
  showInspectionFooterOnLastPage: boolean;
}

export function receivePdfInspectionFooterFlags(
  pageGroups: readonly ReceivePdfPageGroupPlan[],
): boolean[] {
  return pageGroups.flatMap(({ pageCount, showInspectionFooterOnLastPage }) => {
    if (!Number.isInteger(pageCount) || pageCount < 1) {
      throw new RangeError("pageCount must be a positive integer");
    }
    return Array.from(
      { length: pageCount },
      (_, index) => showInspectionFooterOnLastPage && index === pageCount - 1,
    );
  });
}

export interface ReceivePdfPropertyPartition {
  itemProperties: Array<{ name: string; value: string }>;
  finalNoteProperties: Array<{ name: string; value: string }>;
}

export function partitionReceivePdfProperties(
  properties: Array<{ name: string; value: string }>,
): ReceivePdfPropertyPartition {
  const itemProperties: Array<{ name: string; value: string }> = [];
  const finalNoteProperties: Array<{ name: string; value: string }> = [];
  for (const property of properties) {
    const name = property.name.normalize("NFKC").trim().toLowerCase();
    const finalNote = name.includes("備考")
      || name.includes("メモ")
      || name.startsWith("返品・交換不可");
    (finalNote ? finalNoteProperties : itemProperties).push(property);
  }
  return { itemProperties, finalNoteProperties };
}

export function receivePdfLayoutOptions(layout: ReceivePdfLayout): ReceivePdfLayoutOptions {
  return layout === "repair"
    ? { instructionTitle: "お直し指示書", showInspectionFooter: false }
    : { instructionTitle: "", showInspectionFooter: true };
}

export interface ReceivePdfPropertyChunk {
  name: string;
  value: string;
  continued: boolean;
}

export function splitReceivePdfText(
  value: string,
  maxChars: number,
  maxLines = 18,
): string[] {
  if (!Number.isInteger(maxChars) || maxChars < 1 || !Number.isInteger(maxLines) || maxLines < 1) {
    throw new RangeError("maxChars and maxLines must be positive integers");
  }
  const normalized = value.replace(/\r\n?/g, "\n");
  if (normalized.trim().length === 0) return [""];
  const characters = Array.from(normalized);
  const chunks: string[] = [];
  let current: string[] = [];
  let lines = 1;
  for (const character of characters) {
    if (
      current.length >= maxChars
      || (character === "\n" && lines >= maxLines)
    ) {
      chunks.push(current.join(""));
      current = [];
      lines = 1;
    }
    current.push(character);
    if (character === "\n") lines += 1;
  }
  if (current.length > 0) chunks.push(current.join(""));
  return chunks;
}

export function groupReceivePdfProperties(
  properties: Array<{ name: string; value: string }>,
): ReceivePdfPropertyChunk[][] {
  const pieces = properties.flatMap((property) =>
    splitReceivePdfText(property.value, RECEIVE_PDF_PROPERTY_VALUE_CHARS).map(
      (value, index) => ({
        name: property.name,
        value,
        continued: index > 0,
      }),
    )
  );
  const groups: ReceivePdfPropertyChunk[][] = [];
  let current: ReceivePdfPropertyChunk[] = [];
  let currentChars = 0;

  for (const piece of pieces) {
    const pieceChars = Array.from(piece.name).length + Array.from(piece.value).length;
    if (current.length > 0 && currentChars + pieceChars > RECEIVE_PDF_PROPERTY_GROUP_CHARS) {
      groups.push(current);
      current = [];
      currentChars = 0;
    }
    current.push(piece);
    currentChars += pieceChars;
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

export function formatReceivePdfShippedSummary(shippedItemCount: number): string {
  return `出荷済みの商品：${shippedItemCount > 0 ? "あり" : "なし"}`;
}

function twoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

interface TokyoDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function tokyoDateTimeParts(value: Date): TokyoDateTimeParts | null {
  if (!Number.isFinite(value.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const values = new Map(parts.map((part) => [part.type, part.value]));
  const result = {
    year: Number(values.get("year")),
    month: Number(values.get("month")),
    day: Number(values.get("day")),
    hour: Number(values.get("hour")),
    minute: Number(values.get("minute")),
  };
  return Object.values(result).every(Number.isFinite) ? result : null;
}

export function isDailyOrderPrintEligible(createdAt: string, printedAt: Date): boolean {
  const orderParts = tokyoDateTimeParts(new Date(createdAt));
  const printParts = tokyoDateTimeParts(printedAt);
  if (!orderParts || !printParts) return false;
  const orderDate = (orderParts.year * 10_000) + (orderParts.month * 100) + orderParts.day;
  const printDate = (printParts.year * 10_000) + (printParts.month * 100) + printParts.day;
  if (orderDate < printDate) return true;
  if (orderDate > printDate) return false;
  return orderParts.hour < 14 || (orderParts.hour === 14 && orderParts.minute < 2);
}

export const DAILY_ORDER_PRINT_SALES_SOURCES = ["ネット", "大阪", "東京"] as const;
export type DailyOrderPrintSalesSource = typeof DAILY_ORDER_PRINT_SALES_SOURCES[number];

interface DailyOrderPrintSalesSourceOrder {
  id: string;
  salesSource: string;
}

export function filterDailyOrderPrintOrdersBySalesSource<T extends DailyOrderPrintSalesSourceOrder>(
  orders: readonly T[],
  selectedSalesSources: readonly string[],
): { included: T[]; excluded: T[] } {
  const selected = new Set(selectedSalesSources);
  const included: T[] = [];
  const excluded: T[] = [];
  for (const order of orders) {
    (selected.has(order.salesSource) ? included : excluded).push(order);
  }
  return { included, excluded };
}

export function dailyOrderPrintHistoryOrderIds<T extends { id: string }>(
  includedOrders: readonly T[],
  eligibleExcludedOrders: readonly T[],
  moveEligibleExcludedOrders: boolean,
): string[] {
  const ids = new Set(includedOrders.map((order) => order.id));
  if (moveEligibleExcludedOrders) {
    for (const order of eligibleExcludedOrders) ids.add(order.id);
  }
  return [...ids];
}

export type DailyOrderPrintMode = "scheduled" | "selected" | "reprint";

export function resolveDailyOrderPrintMode(
  selectedCount: number,
  view: "active" | "history",
  filter: string,
): DailyOrderPrintMode | null {
  if (view === "active" && filter === "all") {
    return selectedCount > 0 ? "selected" : "scheduled";
  }
  if (view === "history" && filter === "received" && selectedCount > 0) {
    return "reprint";
  }
  return null;
}

export function dailyOrderPrintConditionLabel(mode: DailyOrderPrintMode): string {
  if (mode === "selected") return "選択注文（14:02条件を無視）";
  if (mode === "reprint") return "印刷履歴から選択（再印刷）";
  return "注文日時が当日 14:02 より前（過去分を含む）";
}

export function dailyOrderPrintInventoryMismatchBadgeHtml(
  inventoryMismatch: boolean,
): string {
  return inventoryMismatch
    ? '<em class="daily-print-return-required-badge">在庫<br>相違</em>'
    : "";
}

export function formatDailyOrderPrintPdfFilename(createdAt: Date): string {
  const parts = tokyoDateTimeParts(createdAt);
  if (!parts) return "日々注文リスト.pdf";
  const date = parts.year + "-" + twoDigits(parts.month) + "-" + twoDigits(parts.day);
  const time = twoDigits(parts.hour) + "時" + twoDigits(parts.minute) + "分";
  return "日々注文リスト" + date + "_" + time + ".pdf";
}

export function formatReceivePdfFilename(createdAt: Date): string {
  const date = [
    createdAt.getFullYear(),
    twoDigits(createdAt.getMonth() + 1),
    twoDigits(createdAt.getDate()),
  ].join("-");
  const time = `${twoDigits(createdAt.getHours())}時${twoDigits(createdAt.getMinutes())}分`;
  return `サイズオーダー入荷データ${date}_${time}.pdf`;
}

export function formatRepairPrintPdfFilename(createdAt: Date): string {
  const date = [
    createdAt.getFullYear(),
    twoDigits(createdAt.getMonth() + 1),
    twoDigits(createdAt.getDate()),
  ].join("-");
  const time = `${twoDigits(createdAt.getHours())}時${twoDigits(createdAt.getMinutes())}分`;
  return `お直し印刷データ${date}_${time}.pdf`;
}

